import React from 'react';
import {
  Box, Typography, Popover, Switch, IconButton, Button, Tooltip, Badge, Divider,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import type { NewsSource } from '../../types/News';
import { isSourceEnabled, hasOverrides, SourceOverrides } from '../../utils/newsSourcePrefs';

interface NewsSourceSettingsProps {
  sources: NewsSource[];
  overrides: SourceOverrides;
  onToggle: (source: NewsSource, enabled: boolean) => void;
  onResetDefaults: () => void;
  onEnableAll: () => void;
  onDisableAll: () => void;
}

/**
 * 媒体の表示設定。
 *
 * 一覧をそのまま画面に並べると、記事より設定のほうが場所を取る。
 * アイコンの中に畳んで、デフォルトから変えているときだけバッジで気づけるようにする。
 *
 * 媒体ごとにシステム側のデフォルト (default_enabled) があり、ここで持つのはその差分だけ。
 * だから「すべて解除」ではなく「デフォルトに戻す」が正しい操作になる。
 */
const NewsSourceSettings: React.FC<NewsSourceSettingsProps> = ({
  sources,
  overrides,
  onToggle,
  onResetDefaults,
  onEnableAll,
  onDisableAll,
}) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const enabledCount = sources.filter((s) => isSourceEnabled(s, overrides)).length;
  const customized = hasOverrides(overrides);
  const allEnabled = sources.length > 0 && enabledCount === sources.length;
  const noneEnabled = sources.length > 0 && enabledCount === 0;

  // デフォルトOFFの媒体は下にまとめる。
  // 既定で出ている媒体を先に見せたほうが探しやすい
  const ordered = React.useMemo(
    () => [...sources].sort((a, b) => Number(b.default_enabled) - Number(a.default_enabled)),
    [sources]
  );

  return (
    <>
      <Tooltip title="媒体の表示設定">
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            color: customized ? 'primary.main' : 'rgba(255,255,255,0.55)',
            '&:hover': { color: customized ? 'primary.main' : 'rgba(255,255,255,0.85)' },
          }}
        >
          {/* デフォルトのままなら印を出さない。
              デフォルトOFFの媒体があるだけでは「ユーザーが変更中」ではない */}
          <Badge
            variant="dot"
            invisible={!customized}
            sx={{ '& .MuiBadge-dot': { backgroundColor: 'primary.main' } }}
          >
            <TuneIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 280,
              borderRadius: 3,
              backgroundColor: 'rgba(0,0,0,0.78)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.14)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.75, pb: 1 }}>
          <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600 }}>
            表示する媒体
          </Typography>
          <Typography sx={{
            fontSize: { xs: '0.5rem', sm: '0.55rem' },
            color: 'rgba(255,255,255,0.45)', mt: 0.25,
          }}>
            この端末にのみ保存されます（{enabledCount}/{sources.length}）
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <Box sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
          {ordered.map((source) => {
            const enabled = isSourceEnabled(source, overrides);
            return (
              <Box
                key={source.slug}
                onClick={() => onToggle(source, !enabled)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                      color: enabled ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
                    }}
                    noWrap
                  >
                    {source.name}
                  </Typography>
                  {/* デフォルトでOFFの媒体だけ、その旨を出す。
                      「デフォルトONを自分で切った」側はスイッチを見れば分かるので書かない */}
                  {!source.default_enabled && (
                    <Typography sx={{
                      fontSize: { xs: '0.45rem', sm: '0.5rem' },
                      color: 'rgba(255,255,255,0.35)',
                    }}>
                      デフォルトOFF
                    </Typography>
                  )}
                </Box>
                <Switch
                  size="small"
                  checked={enabled}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onToggle(source, e.target.checked)}
                />
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 1 }}>
          <Button
            size="small"
            onClick={onEnableAll}
            disabled={allEnabled}
            sx={{ fontSize: { xs: '0.5rem', sm: '0.55rem' }, flex: 1, minWidth: 0 }}
          >
            すべて表示
          </Button>
          <Button
            size="small"
            onClick={onDisableAll}
            disabled={noneEnabled}
            sx={{
              fontSize: { xs: '0.5rem', sm: '0.55rem' }, flex: 1, minWidth: 0,
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            すべて解除
          </Button>
          <Button
            size="small"
            onClick={onResetDefaults}
            disabled={!customized}
            sx={{
              fontSize: { xs: '0.5rem', sm: '0.55rem' }, flex: 1, minWidth: 0,
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            デフォルト
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NewsSourceSettings;
